import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import { Button, FormWrapper, Toggle, 
    SelectButton, Checkbox, RadioButton, ButtonType } from '@checkmarx/cx-ui';
import classNames from 'classnames';
import PortalTextBox from '../../portal/portalTextBox';
import TextfieldFloatingText from '../../portal/textfieldFloatingText';
import { LdapEditMode } from '../../../services/enums/ldap';
import AuthnRequestBindingDropdownContainer from './authnRequestBindingDropdown';
import { TooltipTextWrapper } from '../../common/tooltipTextWrapper';
import UserAuthorizationManagementContainer from './userAuthorizationManagement';
import { formatCertificateFileName } from '../../../services/formatters/certificateFileNameFormatter';
import styles from './style.scss';

@inject('translationsStore') @observer
export default class AddEditSAMLForm extends Component {

    samlStore = this.props.samlStore;
    validator = this.props.samlStore.validator;

    handleFiles = e => {

        const file = e.target.files[0];

        if(file) {

            this.samlStore.setCertificateFileName(file.name);
            this.samlStore.setCertificateFile(file);
            this.validator.validateCertificateFilename(this.samlStore.certificateFileName);
        }
    }

    handleChangeActive = e => this.samlStore.setActive(e.target.checked);

    handleChangeName = e => this.samlStore.setName(e.target.value);
    handleNameOnBlur = e => this.validator.validateName(this.samlStore.name);

    handleChangeIssuer = e => this.samlStore.setIssuer(e.target.value);
    handleIssuerOnBlur = e => this.validator.validateIssuer(e.target.value);

    handleChangeLoginUrl = e => this.samlStore.setLoginUrl(e.target.value);
    handleLoginUrlOnBlur = e => this.validator.validateLoginUrl(e.target.value);

    handleChangeLogoutUrl = e => this.samlStore.setLogoutUrl(e.target.value);
    handleLogoutUrlOnBlur = e => this.validator.validateLogoutUrl(e.target.value);

    handleChangeErrorUrl = e => this.samlStore.setErrorUrl(e.target.value);
    handleErrorUrlOnBlur = e => this.validator.validateErrorUrl(e.target.value);

    signAuthnRequestChanged = e => this.samlStore.setSignAuthnRequest(e.target.checked);

    authorizationTypeChanged = e => this.samlStore.setIsManualManagement(e.target.value);

    authnRequestBindingChanged = (e, index, value) => 
            this.samlStore.setAuthnRequestBinding(value != this.samlStore.unassignedRequestBindingId ? value : '');

    onChangeDefaultRoleId = (e, index, value) => this.samlStore.setDefaultRoleId(value);

    render() {

        const t = this.props.translationsStore.translate;
        const { editMode } = this.props;
        const operationInProgress = this.samlStore.postSAMLIdpsProgress || this.samlStore.putSAMLIdpsProgress;
        const authnRequestBindingList = this.samlStore.authnRequestBindingList.slice();
        const certificateFileName = formatCertificateFileName(this.samlStore.certificateFileName, this.samlStore.certificateSubject);

        return(
            <div
                className={styles.samlEditFormMainWrapper}
            >
                <FormWrapper
                    dataCy={this.props.dataCy || 'edit-saml-idp-form'}
                >
                    <div
                        className={classNames(styles.samlEditFormTitle, styles.formBody)}
                        style={{
                            paddingBottom: 0
                        }}
                    >
                        {editMode === LdapEditMode.add ? t('ADD_NEW_SAML_IDENTITY_PROVIDER') : t('UPDATE_SAML_IDENTITY_PROVIDER')}
                    </div>
                    <form onSubmit={this.props.submit}>
                        <div
                            className={styles.formBody}
                            style={{
                                paddingTop: 0
                            }}
                        >
                            <div>
                                <div className='enable-server'>
                                    <Toggle
                                        dataCy='tgl-enable-idp'
                                        className='enable-ldap-server'
                                        icons={false}
                                        onChange={this.handleChangeActive}
                                        disabled={operationInProgress}
                                        checked={this.samlStore.active}
                                        text={t('ENABLE_SAML')}
                                    />
                                </div>
                                <div className={styles.samlFormRow}>
                                    <PortalTextBox
                                        dataCy={'txt-idp-display-name'}
                                        hintText={t('IDENTITY_PROVIDER_DISPLAY_NAME')}
                                        floatingLabelText={<TextfieldFloatingText
                                                                title={t('IDENTITY_PROVIDER_DISPLAY_NAME')}
                                                                required={true}
                                                            />}
                                        width='47%'
                                        disabled={operationInProgress}
                                        onChange={this.handleChangeName}
                                        onBlur={this.handleNameOnBlur}
                                        errorText={this.validator.nameValidationMessage}
                                        value={this.samlStore.name || ''}
                                        helpTooltipText={
                                            <TooltipTextWrapper
                                                text={t('IDENTITY_PROVIDER_DISPLAY_NAME_TOOLTIP')}
                                                exampleTitle={t('EXAMPLE')}
                                                exampleText='CxSAML'
                                            />
                                        }
                                    />
                                    <PortalTextBox
                                        dataCy={'txt-idp-issuer'}
                                        hintText={t('ISSUER_IDENTITY_PROVIDER')}
                                        floatingLabelText={<TextfieldFloatingText
                                                                title={t('ISSUER_IDENTITY_PROVIDER')}
                                                                required={true}
                                                            />}
                                        width='47%'
                                        disabled={operationInProgress}
                                        onChange={this.handleChangeIssuer}
                                        onBlur={this.handleIssuerOnBlur}
                                        errorText={this.validator.issuerValidationMessage}
                                        value={this.samlStore.issuer || ''}
                                        helpTooltipText={
                                            <TooltipTextWrapper
                                                text={t('ISSUER_IDENTITY_PROVIDER_TOOLTIP')}
                                                exampleTitle={t('EXAMPLE')}
                                                exampleText='https://srv1.idpname.com/mypage'
                                            />
                                        }
                                    />
                                </div>
                                <div className={styles.samlFormRow}>
                                    <PortalTextBox
                                        dataCy={'txt-idp-login-url'}
                                        hintText={t('SINGLE_SIGNON_URL')}
                                        floatingLabelText={<TextfieldFloatingText
                                                                title={t('SINGLE_SIGNON_URL')}
                                                                required={true}
                                                            />}
                                        width='47%'
                                        disabled={operationInProgress}
                                        onChange={this.handleChangeLoginUrl}
                                        onBlur={this.handleLoginUrlOnBlur}
                                        errorText={this.validator.loginUrlValidationMessage}
                                        value={this.samlStore.loginUrl || ''}
                                        helpTooltipText={<TooltipTextWrapper
                                            text={t('SINGLE_SIGNON_URL_TOOLTIP')}
                                            exampleTitle={t('EXAMPLE')}
                                            exampleText='https://srv1.idpname.com/app/checkmarxdev/sso/saml'
                                        />}
                                        
                                    />
                                    <PortalTextBox
                                        dataCy={'txt-idp-logout-url'}
                                        hintText={t('LOGOUT_REDIRECT_URL')}
                                        floatingLabelText={<TextfieldFloatingText
                                                                title={t('LOGOUT_REDIRECT_URL')}
                                                            />}
                                        width='47%'
                                        disabled={operationInProgress}
                                        onChange={this.handleChangeLogoutUrl}
                                        onBlur={this.handleLogoutUrlOnBlur}
                                        errorText={this.validator.logoutUrlValidationMessage}
                                        value={this.samlStore.logoutUrl || ''}
                                        helpTooltipText={
                                            <TooltipTextWrapper
                                                text={t('LOGOUT_REDIRECT_URL_TOOLTIP')}
                                                exampleTitle={t('EXAMPLE')}
                                                exampleText='https://srv1.idpname.com/apps'
                                            />
                                        }
                                    />
                                </div>
                                <div className={styles.samlFormRow}>
                                    <PortalTextBox
                                        dataCy={'txt-idp-error-url'}
                                        hintText={t('ERROR_REDIRECT_URL')}
                                        floatingLabelText={<TextfieldFloatingText
                                                                title={t('ERROR_REDIRECT_URL')}
                                                            />}
                                        width='47%'
                                        disabled={operationInProgress}
                                        onChange={this.handleChangeErrorUrl}
                                        onBlur={this.handleErrorUrlOnBlur}
                                        errorText={this.validator.errorUrlValidationMessage}
                                        value={this.samlStore.errorUrl || ''}
                                        helpTooltipText={
                                            <TooltipTextWrapper
                                                text={t('ERROR_REDIRECT_URL_TOOLTIP')}
                                            />
                                        }
                                    />
                                    <SelectButton
                                        dataCy='idp-certificate-fileuploader'
                                        placeHolderText={<TextfieldFloatingText
                                                            title={t('IDP_CERTIFICATE_FILE')}
                                                            required={true}
                                                        />}
                                        disabled={operationInProgress}
                                        width='47%'
                                        style={{top: '10px'}}
                                        buttonText={t('BROWSE')}
                                        title={this.props.teamFullPathName}
                                        isFileInput
                                        fileInputAccept='.crt,.cer,.cert,.pem,.der,.key,.csr,.crl,.pfx,.spc,.p7b,.p7c,.p7r,.p8,.p10,.p12'
                                        fileInputChange={this.handleFiles}
                                        title={certificateFileName}
                                        value={certificateFileName}
                                        errorText={this.validator.certificateFileValidationMessage}
                                        helpTooltipText={t('IDP_CERTIFICATE_FILE_TOOLTIP')}
                                    />
                                </div>
                                <div
                                    style={{
                                        marginTop: 15
                                    }}
                                >
                                    <Checkbox
                                        dataCy='checkbox-sign-authn-request'
                                        onChange={this.signAuthnRequestChanged}
                                        text={t('SIGN_SAML_AUTHNREQUEST')}
                                        checked={this.samlStore.signAuthnRequest}
                                        disabled={operationInProgress}
                                    />
                                </div>
                                <div className={styles.samlFormRow}>
                                    <AuthnRequestBindingDropdownContainer
                                        dataCy={'authn-request-binding-ddl'}
                                        hintText={t('REQUEST_BINDING')}
                                        floatingLabelText={<TextfieldFloatingText
                                                                title={t('REQUEST_BINDING')}
                                                            />}
                                        width='47%'
                                        items={authnRequestBindingList}
                                        disabled={operationInProgress}
                                        onChange={this.authnRequestBindingChanged}
                                        value={this.samlStore.authnRequestBinding.toUpperCase()}
                                        helpTooltipText={t('REQUEST_BINDING_TOOLTIP')}
                                        defaultSelectionTitle={t('REQUEST_BINDING')}
                                    />
                                </div>
                                <div className='sub-title'>
                                    {t('USER_AUTHORIZATION_MANAGEMENT')}
                                </div>
                                <div
                                    style={{
                                        marginTop: 25
                                    }}
                                >
                                    <div>
                                        <RadioButton
                                            dataCy='radiobutton-manual-authorization'
                                            text={t('APPLICATION_AUTHORIZATION')}
                                            name='idp'
                                            defaultChecked={this.samlStore.isManualManagement}
                                            value={true}
                                            onChange={this.authorizationTypeChanged}
                                            disabled={operationInProgress}
                                        />
                                    </div>
                                    <div>
                                        <RadioButton
                                            dataCy='radiobutton-automatic-authorization'
                                            text={t('IDP_AUTHORIZATION')}
                                            name='idp'
                                            defaultChecked={!this.samlStore.isManualManagement}
                                            value={false}
                                            onChange={this.authorizationTypeChanged}
                                            disabled={operationInProgress}
                                        />
                                    </div>
                                </div>
                                <UserAuthorizationManagementContainer
                                    {...this.props}
                                />
                            </div>
                        </div>
                        <div
                            className={classNames(styles.samlFormButtonsWrapper, styles.formBody)}
                        >
                            <span
                                data-cy='idp-cancel-button'
                                className='portal-blank-button'
                                onClick={this.props.cancelButtonHandler}
                            >
                                {t('CANCEL')}
                            </span>
                            <Button 
                                dataCy='idp-save-button'
                                type='submit'
                                continueButtonText={'continueButtonText'}
                                saveContinueButtonText={'saveContinueButtonText'}
                                saveButtonText={'saveButtonText'}
                                inProgress={operationInProgress}
                                text={t('SAVE')}
                            >
                            </Button>
                        </div>
                    </form>
                </FormWrapper>
            </div>
        );
    }
}